import React, { useState } from 'react'
import {
  Box,
  Button,
  Heading,
  Typography,
  Frame,
  SelectField,
} from 'components'
import { Chip, makeStyles } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { useSpokenLanguages, useContent } from 'utils'
import { ProgressBarFooter } from './FreelancerProgressBarFooter'
import { updateUser } from 'utils'
import firebase from 'utils/firebase'

const useStyles = makeStyles((theme) => ({
  languageContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  languageChipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
    gap: theme.spacing(1),
  },
  languageChip: {
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.primary.main,
    color: 'white',
  },
  languageHeading: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  }
}))

const FreelancerLanguageSelect = ({
  onboarding,
  user,
  stepIndex,
  totalSteps,
  onNext = () => { },
  onSubmit = () => { }
}) => {
  const classes = useStyles()
  const [selectedLanguages, setSelectedLanguages] = useState(user?.spokenLanguages || [])
  const [currentLanguage, setCurrentLanguage] = useState('')
  const spokenLanguages = useSpokenLanguages()
  const { landing } = useContent() || {}
  const buttonColor = landing?.['onboarding-button-color'] || 'primary'
  const buttonSecondaryColor = landing?.['onboarding-button-secondary'] || '#6c757d'

  // Handler to remove a language
  const handleRemoveLanguage = (languageId) => {
    setSelectedLanguages(selectedLanguages.filter(lang => lang.id !== languageId));
  };

  // Display a loading state instead of null to avoid component flashing
  if (!spokenLanguages) {
    return (
      <Frame maxWidth="sm" user={user}>
        <Box mx="auto" maxWidth={530} display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <Typography>Loading languages...</Typography>
        </Box>
      </Frame>
    )
  }

  // Store selected language objects rather than just names
  const handleAddLanguage = () => {
    if (currentLanguage && !selectedLanguages.some(lang => lang.id === currentLanguage)) {
      const selectedLang = spokenLanguages.find(lang => lang.id === currentLanguage);
      if (selectedLang) {
        setSelectedLanguages([...selectedLanguages, selectedLang])
        // console.log(selectedLanguages)
        // console.log(selectedLang)
        setCurrentLanguage('')
      }
    }
  }

  const handleSubmit = () => {
    // Track analytics if in onboarding flow
    if (onboarding) {
      firebase.analytics().logEvent('user_languages_selected', {
        count: selectedLanguages.length
      })
    }

    // console.log('Updating user with languages:', selectedLanguages);

    // Create a clean array of language objects for storage
    const cleanLanguages = selectedLanguages.map(lang => ({
      id: lang.id,
      name: lang.name
    }));

    // console.log('Cleaned languages for storage:', cleanLanguages);

    // Use direct Firebase update with set and merge to ensure data is saved properly
    firebase
      .firestore()
      .collection('users')
      .doc(user.id)
      .set({
        spokenLanguages: cleanLanguages
      }, { merge: true })
      .then(() => {
        // console.log('Languages successfully updated in database')
        if (onNext) {
          onNext()
        }
      })
      .catch(error => {
        console.error('Error updating spoken languages:', error)
      })
  }

  return (
    <Frame maxWidth="sm" user={user}>
      <Box mx="auto" maxWidth={530}>
        <Box flex={1} flexVariant="column center" mb={2.5}>
          <Heading>
            What languages do you speak?
          </Heading>
        </Box>

        <Box className={classes.languageContainer} display="flex" flexDirection="column">
          <Box mb={3}>
            <SelectField
              value={currentLanguage}
              onChange={(e) => setCurrentLanguage(e.target.value)}
              label="Select a language"
              choices={spokenLanguages && spokenLanguages.length > 0 ?
                spokenLanguages.map(language => ({
                  value: language.id,
                  label: language.name
                })) : [
                  { value: 'en', label: 'English' },
                  { value: 'es', label: 'Spanish' },
                  { value: 'fr', label: 'French' },
                  { value: 'zh', label: 'Mandarin' },
                  { value: 'ar', label: 'Arabic' }
                ]}
            />
          </Box>

          <Box mb={3}>
            <Button
              variant="contained"
              style={{ 'background-color': buttonColor }}
              onClick={handleAddLanguage}
              disabled={!currentLanguage}
              fullWidth
            >
              Add Language
            </Button>
          </Box>

          {selectedLanguages.length > 0 && (
            <Box mt={3} className={classes.languageChipsContainer}>
              {selectedLanguages.map((language) => (
                <Chip
                  key={language.id}
                  label={language.name}
                  className={classes.languageChip}
                  onDelete={() => handleRemoveLanguage(language.id)}
                  deleteIcon={<CloseIcon style={{ color: 'white' }} />}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* {onboarding && (
          <Box my={2} flexVariant="row justify-space-evenly">
            <Button variant="contained" style={{ width: '40%', 'background-color': buttonSecondaryColor, color: 'white' }} onClick={() => onNext()}>Skip</Button>
            <Button variant="contained" style={{ width: '40%', 'background-color': buttonColor }} onClick={handleSubmit}>Save</Button>
          </Box>
        )} */}

        {!onboarding && (
          <Box my={2} flexVariant="row justify-space-evenly">
            <Button variant="contained" style={{ width: '40%', 'background-color': buttonColor }} onClick={handleSubmit}>Save</Button>
          </Box>
        )}
      </Box>

      {onboarding && (
        <ProgressBarFooter
          valid={true}
          stepIndex={stepIndex}
          firstStep={stepIndex}
          numSteps={totalSteps}
          onNext={handleSubmit}
          onPrev={() => { }}
        />
      )}
    </Frame>
  )
}

export default FreelancerLanguageSelect
